import React, { useState } from 'react'
import { Card, CardContent, Box, Typography, Checkbox, Chip, IconButton, Tooltip, Collapse } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { INTEGRATIONS, SOURCE_CHIP_SX, sourceStringToIntegrationCode } from '../../shared/constants/integrations'

export interface ActivityResult {
    _id: string
    title: string
    titleLong?: string
    studyNumber?: string
    category?: string
    function?: string
    indication?: string
    genericName?: string
    source: string
    status?: string
    description?: string
    startDate?: string
    endDate?: string
    plannedStartDate?: string
    plannedEndDate?: string
    therapeuticArea?: string
    compoundName?: string
    affiliateName?: string
    providerName?: string
    sponsorType?: string
    countries?: string[]
    studyReportDate?: string
    terminationDate?: string
    studyOwner?: string
    vendorDetails?: string
    vendor?: boolean
    type?: string
    population?: string
    externalId?: string
    lastSyncedAt?: string
    updatedAt?: string
    chipColor?: string
    statusColor?: string
    isImported?: boolean
    inAtlas?: boolean
    roadmapNames?: string[]
}

interface ActivityResultCardProps {
    activity: ActivityResult
    selected?: boolean
    onSelect?: (id: string, checked: boolean) => void
}

const ActivityResultCard: React.FC<ActivityResultCardProps> = ({ activity, selected = false, onSelect }) => {
    const [expanded, setExpanded] = useState(false)
    const hasExpandableContent = !!activity.description
    const [firstRoadmap, ...remainingRoadmaps] = activity.roadmapNames ?? []
    // Re-importing an external activity overwrites its fields with fresh search-result data.
    // Internal activities are just join rows, so re-import is a no-op there.
    const isExternalSource = activity.source === 'GSL' || activity.source === 'Planisware'
    const canOverwrite = !!activity.isImported && isExternalSource

    // A study can live in more than one place: an external integration source (GSL/Planisware)
    // and/or Atlas itself (imported into one or more roadmaps). Show a chip for each so a merged
    // result reads as e.g. [GSL] [Atlas].
    const sourceCode = sourceStringToIntegrationCode(activity.source)
    const sourceChips: Array<{ label: string; color?: string }> = []
    if (sourceCode && INTEGRATIONS[sourceCode].isExternal) {
        sourceChips.push({ label: INTEGRATIONS[sourceCode].label, color: activity.chipColor })
    }
    if (activity.inAtlas || activity.source === 'Atlas') {
        sourceChips.push({ label: INTEGRATIONS.atlas.label, color: INTEGRATIONS.atlas.chipColor })
    }

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'row',
                px: 1.5,
                py: 1.25,
                mb: 1.5,
                borderRadius: 2,
                boxShadow: 'none',
                border: '1px solid',
                borderColor: selected ? 'figma.primary' : 'figma.grayscale.tier7',
                alignItems: 'flex-start',
                backgroundColor: selected ? 'figma.primary3' : 'figma.grayscale.tier7',
                transition: 'background-color 0.2s ease, border-color 0.2s ease',
            }}
        >
            <CardContent sx={{ flexGrow: 1, p: 0, '&:last-child': { pb: 0 } }}>
                {/* Title + source badge */}
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    {activity.isImported && !canOverwrite ? (
                        <Tooltip title="Already on this roadmap">
                            <CheckCircleOutlineIcon
                                fontSize="small"
                                sx={{ mx: '9px', flexShrink: 0, color: 'figma.accentColors.green' }}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip
                            title={canOverwrite ? 'Already on this roadmap — re-importing will overwrite its data' : ''}
                            disableHoverListener={!canOverwrite}
                        >
                            <Checkbox
                                edge="start"
                                checked={selected}
                                onChange={(e) => onSelect?.(activity._id, e.target.checked)}
                                color="primary"
                                size="small"
                            />
                        </Tooltip>
                    )}
                    <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, color: 'primary.main', flexGrow: 1, pr: 2, lineHeight: 1.4 }}
                    >
                        {activity.title}
                    </Typography>
                    {sourceChips.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                            {sourceChips.map((chip) => (
                                <Chip
                                    key={chip.label}
                                    label={chip.label}
                                    size="small"
                                    sx={{
                                        ...SOURCE_CHIP_SX(chip.color),
                                        flexShrink: 0,
                                        fontSize: '0.7rem',
                                        height: 22,
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, pl: '25px' }}>
                    {/* Category subtitle */}
                    {(activity.category ||
                        canOverwrite ||
                        (activity.roadmapNames && activity.roadmapNames.length > 0)) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                            {activity.category && (
                                <Typography variant="body2" sx={{ color: 'figma.grayscale.tier3', fontSize: '0.8rem' }}>
                                    {activity.category}
                                </Typography>
                            )}
                            {canOverwrite && (
                                <Tooltip title="Already on this roadmap — re-importing will overwrite its data">
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.25,
                                            color: 'figma.accentColors.green',
                                            cursor: 'default',
                                        }}
                                    >
                                        <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                                        <Typography
                                            variant="body2"
                                            sx={{ fontSize: '0.7rem', fontWeight: 600, lineHeight: 1 }}
                                        >
                                            On this roadmap
                                        </Typography>
                                    </Box>
                                </Tooltip>
                            )}
                            {firstRoadmap && (
                                <Tooltip title="Imported into other ATLAS roadmap">
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            ml: 'auto',
                                            cursor: 'default',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ color: 'figma.grayscale.tier3', fontSize: '0.7rem' }}
                                        >
                                            {firstRoadmap}
                                        </Typography>
                                        {remainingRoadmaps.length > 0 && (
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'figma.grayscale.tier3', fontSize: '0.7rem' }}
                                            >
                                                +{remainingRoadmaps.length}
                                            </Typography>
                                        )}
                                    </Box>
                                </Tooltip>
                            )}
                        </Box>
                    )}

                    {/* Metadata rows */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {activity.status && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: 'figma.grayscale.tier1' }}>
                                    Status:
                                </Typography>
                                <Chip
                                    label={activity.status}
                                    size="small"
                                    sx={{
                                        backgroundColor: activity.statusColor
                                            ? `${activity.statusColor}22`
                                            : 'figma.grayscale.tier6',
                                        color: activity.statusColor ?? 'figma.grayscale.tier2',
                                        fontWeight: 500,
                                        fontSize: '0.75rem',
                                        height: 22,
                                    }}
                                />
                            </Box>
                        )}

                        {activity.indication && (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: 'figma.grayscale.tier1', flexShrink: 0 }}
                                >
                                    Indication:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {activity.indication}
                                </Typography>
                            </Box>
                        )}

                        {(activity.genericName || activity.function) && (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'figma.grayscale.tier1',
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {activity.genericName ? 'Generic Name [Compound]:' : 'Function:'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {activity.genericName ?? activity.function}
                                </Typography>
                            </Box>
                        )}

                        {activity.studyReportDate && (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: 'figma.grayscale.tier1', flexShrink: 0 }}
                                >
                                    FSR Actual Date:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {new Date(activity.studyReportDate).toLocaleDateString('en-US', {
                                        timeZone: 'UTC',
                                    })}
                                </Typography>
                            </Box>
                        )}

                        {activity.type && (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: 'figma.grayscale.tier1', flexShrink: 0 }}
                                >
                                    Study Type:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {activity.type}
                                </Typography>
                            </Box>
                        )}

                        {activity.studyNumber && (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: 'figma.grayscale.tier1', flexShrink: 0 }}
                                >
                                    Study Number:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {activity.studyNumber}
                                </Typography>
                            </Box>
                        )}

                        {activity.externalId && (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: 'figma.grayscale.tier1', flexShrink: 0 }}
                                >
                                    External ID:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {activity.externalId}
                                </Typography>
                            </Box>
                        )}

                        {activity.lastSyncedAt && (
                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 700, color: 'figma.grayscale.tier1', flexShrink: 0 }}
                                >
                                    Last Synced:
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {new Date(activity.lastSyncedAt).toLocaleDateString()}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Box>

                {/* Expandable Objective */}
                {hasExpandableContent && (
                    <Collapse in={expanded}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'figma.grayscale.tier1' }}>
                                Objective:
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ whiteSpace: 'pre-line', fontSize: '0.8rem' }}
                            >
                                {activity.description}
                            </Typography>
                        </Box>
                    </Collapse>
                )}

                {/* Bottom action icons */}
                {hasExpandableContent && (
                    <Box
                        sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.25, mt: 0.75 }}
                    >
                        <Tooltip title={expanded ? 'Collapse' : 'Expand'}>
                            <IconButton
                                size="small"
                                onClick={() => setExpanded((prev) => !prev)}
                                sx={{
                                    transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.2s ease',
                                }}
                            >
                                <ExpandMoreIcon sx={{ fontSize: 16, color: 'figma.grayscale.tier3' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}
            </CardContent>
        </Card>
    )
}

export default ActivityResultCard
